// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Injectable } from '@angular/core';
import { HceFileManagementService, HceResponseType } from '../hce-file-management/hce-file-management.service';
import { Observable, of, map, switchMap, throwError } from 'rxjs';
import { HceRefGeneExonRaw, HceView } from '../../../interfaces/hce/hce-reference';
import { FileMeta, HceApiService } from '../hce-api/hce-api.service';
import { Utils } from '../hce-parser/utils';

interface Config {
  reference: string;
  parameters: string;
}

interface Reference {
  reference_map: string;
  view: string;
  ref_gene: string;
  ref_gene_exon_intron: string;
  refs_name_mapping: string;
  ucsc_ref_version: string;
}

@Injectable({ providedIn: 'root' })
export class HceReferenceService {
  // ===== Constructor & DI =====
  constructor(
    private readonly api: HceApiService,
    private readonly file: HceFileManagementService,
  ) {}

  // ===== Public API: Content Retrieval =====
  public getReferenceMap(panelId: number, responseType: HceResponseType): Observable<ArrayBuffer | Blob | string>;
  public getReferenceMap(panelId: number, responseType: 'arraybuffer'): Observable<ArrayBuffer>;
  public getReferenceMap(panelId: number, responseType: 'blob'): Observable<Blob>;
  public getReferenceMap(panelId: number, responseType: 'text'): Observable<string>;
  public getReferenceMap(panelId: number): Observable<string>;
  public getReferenceMap(panelId: number, responseType: HceResponseType = 'text'): Observable<ArrayBuffer | Blob | string> {
    return this.getRefFileByKey(panelId, 'reference_map', responseType);
  }

  public getView(panelId: number, responseType: HceResponseType): Observable<ArrayBuffer | Blob | string>;
  public getView(panelId: number, responseType: 'arraybuffer'): Observable<ArrayBuffer>;
  public getView(panelId: number, responseType: 'blob'): Observable<Blob>;
  public getView(panelId: number, responseType: 'text'): Observable<string>;
  public getView(panelId: number): Observable<string>;
  public getView(panelId: number, responseType: HceResponseType = 'text'): Observable<ArrayBuffer | Blob | string> {
    return this.getRefFileByKey(panelId, 'view', responseType);
  }
  public getViewTsv(panelId: number): Observable<HceView[]> {
    return this.getView(panelId).pipe(
      map(text => Utils.tsv.parse(text)),
      map(records => {
        return records.filter(cols => {
            const c0 = (cols[0] ?? '').trim();
            if (c0.startsWith('#')) return false;
            if (c0 === 'chrM') return false;
            return true;
          })
          .map(cols => {
            return {
              title: cols[0] ?? '',
              start: cols[1] ?? '',
              end: cols[2] ?? '',
              name: cols[3] ?? '',
              gieStain: cols[4] ?? '',
            } as HceView;
          });
      }),
    );
  }

  public getRefGene(panelId: number, responseType: HceResponseType): Observable<ArrayBuffer | Blob | string>;
  public getRefGene(panelId: number, responseType: 'arraybuffer'): Observable<ArrayBuffer>;
  public getRefGene(panelId: number, responseType: 'blob'): Observable<Blob>;
  public getRefGene(panelId: number, responseType: 'text'): Observable<string>;
  public getRefGene(panelId: number): Observable<string>;
  public getRefGene(panelId: number, responseType: HceResponseType = 'text'): Observable<ArrayBuffer | Blob | string> {
    return this.getRefFileByKey(panelId, 'ref_gene', responseType);
  }

  public getRefGeneExonIntron(panelId: number, responseType: HceResponseType): Observable<ArrayBuffer | Blob | string>;
  public getRefGeneExonIntron(panelId: number, responseType: 'arraybuffer'): Observable<ArrayBuffer>;
  public getRefGeneExonIntron(panelId: number, responseType: 'blob'): Observable<Blob>;
  public getRefGeneExonIntron(panelId: number, responseType: 'text'): Observable<string>;
  public getRefGeneExonIntron(panelId: number): Observable<string>;
  public getRefGeneExonIntron(panelId: number, responseType: HceResponseType = 'text'): Observable<ArrayBuffer | Blob | string> {
    return this.getRefFileByKey(panelId, 'ref_gene_exon_intron', responseType);
  }
  public getRefGeneExonIntronTsv(panelId: number): Observable<HceRefGeneExonRaw[]> {
    return this.getRefGeneExonIntron(panelId).pipe(
      map(text => Utils.tsv.parse(text)),
      map(records => {
        if (!records) return [];
        return records.filter(cols => {
          const c0 = (cols[0] ?? '').trim();
          if (c0.startsWith('#')) return false;
          return true;
        })
        .map(cols => ({
          chr: (cols?.[0] ?? ''),
          start: (cols?.[1] ?? ''),
          end: (cols?.[2] ?? ''),
          id: (cols?.[3] ?? ''),
          direction: (cols?.[5] ?? ''),
          exonCount: (cols?.[9] ?? ''),
          exonLength: (cols?.[10] ?? ''),
          exonStart: (cols?.[11] ?? ''),
        }))
      }),
    );
  }

  public getRefsNameMapping(panelId: number, responseType: HceResponseType): Observable<ArrayBuffer | Blob | string>;
  public getRefsNameMapping(panelId: number, responseType: 'arraybuffer'): Observable<ArrayBuffer>;
  public getRefsNameMapping(panelId: number, responseType: 'blob'): Observable<Blob>;
  public getRefsNameMapping(panelId: number, responseType: 'text'): Observable<string>;
  public getRefsNameMapping(panelId: number): Observable<string>;
  public getRefsNameMapping(panelId: number, responseType: HceResponseType = 'text'): Observable<ArrayBuffer | Blob | string> {
    return this.getRefFileByKey(panelId, 'refs_name_mapping', responseType);
  }
  public getRefsNameMappingJson(panelId: number): Observable<Record<string, string>> {
    return this.getRefsNameMapping(panelId).pipe(map(text => this.parseJson(text)));
  }

  public getUcscRefVersion(panelId: number): Observable<string> {
    return this.getReferenceForPanel(panelId).pipe(
      map(reference => reference?.ucsc_ref_version ?? 'hg38'),
    );
  }

  // ===== Public API: Metadata Retrieval =====
  public getReferenceMapFileMeta(panelId: number): Observable<FileMeta> {
    return this.getRefFileMetaByKey(panelId, 'reference_map');
  }
  public getReferenceMapFileName(panelId: number): Observable<string> {
    return this.getRefFileNameByKey(panelId, 'reference_map');
  }
  public getReferenceMapFileSize(panelId: number): Observable<number | null> {
    return this.getRefFileSizeByKey(panelId, 'reference_map');
  }

  public getViewFileMeta(panelId: number): Observable<FileMeta> {
    return this.getRefFileMetaByKey(panelId, 'view');
  }
  public getViewFileName(panelId: number): Observable<string> {
    return this.getRefFileNameByKey(panelId, 'view');
  }
  public getViewFileSize(panelId: number): Observable<number | null> {
    return this.getRefFileSizeByKey(panelId, 'view');
  }

  public getRefGeneFileMeta(panelId: number): Observable<FileMeta> {
    return this.getRefFileMetaByKey(panelId, 'ref_gene');
  }
  public getRefGeneFileName(panelId: number): Observable<string> {
    return this.getRefFileNameByKey(panelId, 'ref_gene');
  }
  public getRefGeneFileSize(panelId: number): Observable<number | null> {
    return this.getRefFileSizeByKey(panelId, 'ref_gene');
  }

  public getRefGeneExonIntronFileMeta(panelId: number): Observable<FileMeta> {
    return this.getRefFileMetaByKey(panelId, 'ref_gene_exon_intron');
  }
  public getRefGeneExonIntronFileName(panelId: number): Observable<string> {
    return this.getRefFileNameByKey(panelId, 'ref_gene_exon_intron');
  }
  public getRefGeneExonIntronFileSize(panelId: number): Observable<number | null> {
    return this.getRefFileSizeByKey(panelId, 'ref_gene_exon_intron');
  }

  public getRefsNameMappingFileMeta(panelId: number): Observable<FileMeta> {
    return this.getRefFileMetaByKey(panelId, 'refs_name_mapping');
  }
  public getRefsNameMappingFileName(panelId: number): Observable<string> {
    return this.getRefFileNameByKey(panelId, 'refs_name_mapping');
  }
  public getRefsNameMappingFileSize(panelId: number): Observable<number | null> {
    return this.getRefFileSizeByKey(panelId, 'refs_name_mapping');
  }

  // ===== Private helpers: Content helpers =====
  getReference(name: string): Observable<Reference> {
    return this.file.getCommonFile('refs.json').pipe(
      map(text => this.parseJson<Record<string, Reference>>(text)),
      switchMap(refs => {
        if (refs?.[name] == null) {
          const msg = `Reference '${name}' is not defined in refs.json.`;
          return throwError(() => new Error(msg));
        }
        return of(refs[name]);
      }),
    );
  }

  getReferenceName(panelId: number): Observable<string> {
    return this.file.getPanelFile(panelId, 'config.json').pipe(
      map(text => this.parseJson<Config>(text)),
      switchMap(config => {
        if (config?.reference == null) {
          const msg = `config.json does not define a 'reference' field (panelId=${panelId})`;
          return throwError(() => new Error(msg));
        }
        return of(config.reference);
      })
    );
  }
  
  private getReferenceForPanel(panelId: number): Observable<Reference> {
    return this.getReferenceName(panelId).pipe(
      switchMap(name => this.getReference(name))
    );
  }

  private getRefFileByKey<K extends keyof Reference>(
    panelId: number,
    key: K,
    responseType: HceResponseType,
  ): Observable<ArrayBuffer>;
  private getRefFileByKey<K extends keyof Reference>(
    panelId: number,
    key: K,
    responseType: 'arraybuffer',
  ): Observable<ArrayBuffer>;
  private getRefFileByKey<K extends keyof Reference>(
    panelId: number,
    key: K,
    responseType: 'blob',
  ): Observable<Blob>;
  private getRefFileByKey<K extends keyof Reference>(
    panelId: number,
    key: K,
    responseType: 'text',
  ): Observable<string>;
  private getRefFileByKey<K extends keyof Reference>(
    panelId: number,
    key: K,
    responseType: HceResponseType,
  ): Observable<ArrayBuffer | Blob | string> {
    return this.getReferenceForPanel(panelId).pipe(
      switchMap(ref => {
        const path = ref[key];
        if (!path) {
          const msg = `Missing required field '${String(key)}' in reference definition (refs.json entry).`;
          return throwError(() => new Error(msg));
        }
        return this.file.getCommonFile(`refs/${path}`, responseType);
      })
    );
  }

  private parseJson<T extends object>(
    text: string,
    fallback: T = {} as T,
  ): T {
    try {
      const v = JSON.parse(text);
      return (v && typeof v === 'object') ? (v as T) : fallback;
    } catch {
      return fallback;
    }
  }

  // ===== Private helpers: Metadata helpers =====
  private getRefSignedUrlByKey<K extends keyof Reference>(
    panelId: number,
    key: K,
  ): Observable<string> {
    return this.getReferenceForPanel(panelId).pipe(
      switchMap(ref => {
        const path = ref[key];
        if (!path) {
          const msg = `Missing required field '${String(key)}' in reference definition (refs.json entry).`;
          return throwError(() => new Error(msg));
        }
        return this.file.getCommonSignedUrl(`refs/${path}`);
      })
    );
  }

  private getRefFileMetaByKey<K extends keyof Reference>(
    panelId: number,
    key: K,
  ): Observable<FileMeta> {
    return this.getRefSignedUrlByKey(panelId, key).pipe(
      switchMap(url => this.api.getFileMeta(url))
    );
  }

  private getRefFileNameByKey<K extends keyof Reference>(
    panelId: number,
    key: K,
  ): Observable<string> {
    return this.getRefSignedUrlByKey(panelId, key).pipe(
      switchMap(url => this.api.getFileName(url))
    );
  }

  private getRefFileSizeByKey<K extends keyof Reference>(
    panelId: number,
    key: K,
  ): Observable<number | null> {
    return this.getRefSignedUrlByKey(panelId, key).pipe(
      switchMap(url => this.api.getFileSize(url))
    );
  }
}
